import {
  ABROAD_COUNTRIES,
  ABROAD_LINKS,
  type AbroadHome,
  type AbroadStudent,
  type AbroadTrack,
  type LessonItem,
  type LessonFit,
  SCHOOL_TIME_ZONE,
  addDays,
  clockOf,
  countryOf,
  isUkrainianStudies,
  isoWeekday,
  lessonFit,
  placeOf,
  preparesForNmt,
  shortDate,
  timeOf,
  todayIso,
  useAbroadActions,
  useAbroadHome,
  useAbroadStudents,
  useLessons,
  usePeople,
  weekStart,
  weeksAhead,
  zoneShiftOf,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { BuildingsIcon } from '@phosphor-icons/react/Buildings';
import { MoonIcon } from '@phosphor-icons/react/Moon';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentContext } from '../app/ViewContext';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
  ringButton,
  ringSmall,
} from '../design/forms';
import {
  ArrowUpRightIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  type FilledIcon,
  PlusIcon,
  VideoCameraIcon,
} from '../design/icons';
import { useMinute } from '../lib/now';
import { FIELD, Label } from './LifeArt';

/*
 * «Навчання з-за кордону» in «Форми 2» (28.10). For the family: the title on the paper; the family's
 * clock on a soft sky card with the globe quiet behind (their time big, the school's under it, the form
 * of learning as a label); the week by their clock as a ruled list by days — the time, the subject's
 * picture, how the lesson fits as a soft label (convenient — lime, the local school — stone, night —
 * lavender); at the side the subjects with their pictures, where they live (the form), the НМТ on
 * cobalt, the useful links with ↗. For the school: how many and from where on the left, the children as
 * a ruled list on the right. No flags (emoji): the countries by name.
 */

/** «1 вересня 2026 р.» */
const longDate = (iso: string) =>
  new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${iso}T00:00:00Z`));

/** The city, or the country — no flag. */
const cityOf = (h: { country: string; city: string | null }) =>
  h.city ?? countryOf(h.country)?.name ?? h.country;

/** «Київ попереду на 1 год» */
export function zoneShift(t: TFunction, timeZone: string, now = new Date()) {
  const shift = zoneShiftOf(timeZone, now);
  return t(shift.key, { hours: shift.hours });
}

export { placeOf };

/** How a lesson fits the family's day: a soft label in its colour. */
const FIT: Record<LessonFit, { tone: Grad; icon: FilledIcon }> = {
  live: { tone: 'lime', icon: VideoCameraIcon },
  school: { tone: 'stone', icon: BuildingsIcon },
  night: { tone: 'lavender', icon: MoonIcon },
};

function FitBadge({ fit }: { fit: LessonFit }) {
  const { t } = useTranslation();
  return (
    <Label tone={FIT[fit].tone} icon={FIT[fit].icon}>
      {t(`abroad.fit.${fit}`)}
    </Label>
  );
}

/** A switch: on — the dark track, the knob to the right. */
function Switch({
  on,
  label,
  onChange,
}: {
  on: boolean;
  label: string;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="flex items-center gap-3 text-left"
    >
      <span
        className="relative h-[26px] w-[44px] shrink-0 rounded-full transition-colors duration-200"
        style={{ backgroundColor: on ? 'var(--sh-dark)' : '#E4E0D7' }}
      >
        <span
          className="absolute top-[3px] size-5 rounded-full bg-white transition-[left] duration-300 ease-spring"
          style={{ left: on ? 21 : 3 }}
        />
      </span>
      <span className="text-[15px]" style={{ color: INK }}>
        {label}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// Where the family lives: the family keeps it, the school also sets the form
// ---------------------------------------------------------------------------------------------

export interface HomeDraft {
  country: string;
  city: string;
  timezone: string;
  local: boolean;
  localFrom: string;
  localTo: string;
  track: AbroadTrack;
}

export const draftOf = (h?: AbroadHome | null): HomeDraft => ({
  country: h?.country ?? 'PL',
  city: h?.city ?? '',
  timezone: h?.timezone ?? 'Europe/Warsaw',
  local: Boolean(h?.localFrom),
  localFrom: h?.localFrom ?? '08:00',
  localTo: h?.localTo ?? '14:00',
  track: h?.track ?? 'ukrainian',
});

const fieldLabel = 'mb-1.5 block text-[13px] font-medium text-ink-soft';
const field = `h-11 w-full px-3 ${FIELD}`;

function HomeFields({
  draft,
  onChange,
  withTrack,
}: {
  draft: HomeDraft;
  onChange: (d: HomeDraft) => void;
  withTrack?: boolean;
}) {
  const { t } = useTranslation();
  const country = countryOf(draft.country);
  return (
    <div className="flex flex-col gap-3.5">
      {withTrack && (
        <label className="block">
          <span className={fieldLabel}>{t('abroad.form')}</span>
          <select
            className={field}
            value={draft.track}
            onChange={(e) => onChange({ ...draft, track: e.target.value as AbroadTrack })}
          >
            {(['ukrainian', 'full'] as const).map((k) => (
              <option key={k} value={k}>
                {t(`abroad.track.${k}`)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="block min-w-0">
          <span className={fieldLabel}>{t('abroad.country')}</span>
          <select
            className={field}
            value={draft.country}
            onChange={(e) => {
              const next = countryOf(e.target.value);
              onChange({
                ...draft,
                country: e.target.value,
                timezone: next?.zones[0]?.id ?? draft.timezone,
              });
            }}
          >
            {ABROAD_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-0">
          <span className={fieldLabel}>{t('abroad.city')}</span>
          <input
            className={field}
            value={draft.city}
            maxLength={100}
            onChange={(e) => onChange({ ...draft, city: e.target.value })}
          />
        </label>
      </div>
      {country && country.zones.length > 1 && (
        <label className="block">
          <span className={fieldLabel}>{t('abroad.zone')}</span>
          <select
            className={field}
            value={draft.timezone}
            onChange={(e) => onChange({ ...draft, timezone: e.target.value })}
          >
            {country.zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <Switch
        on={draft.local}
        label={t('abroad.localSchool')}
        onChange={(local) => onChange({ ...draft, local })}
      />
      {draft.local && (
        <div className="flex items-center gap-2 text-[14px]" style={{ color: INK_SOFT }}>
          <span>{t('abroad.localFrom')}</span>
          <input
            type="time"
            aria-label={t('abroad.localFrom')}
            className={`h-11 w-32 px-3 ${FIELD}`}
            value={draft.localFrom}
            onChange={(e) => onChange({ ...draft, localFrom: e.target.value })}
          />
          <span>{t('abroad.localTo')}</span>
          <input
            type="time"
            aria-label={t('abroad.localTo')}
            className={`h-11 w-32 px-3 ${FIELD}`}
            value={draft.localTo}
            onChange={(e) => onChange({ ...draft, localTo: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

export const formOf = (studentId: string, d: HomeDraft) => ({
  studentId,
  track: d.track,
  country: d.country,
  city: d.city,
  timezone: d.timezone,
  localFrom: d.local && d.localFrom < d.localTo ? d.localFrom : null,
  localTo: d.local && d.localFrom < d.localTo ? d.localTo : null,
});

function FamilyHome({ home }: { home: AbroadHome }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useAbroadActions(ctx.schoolId);
  const [draft, setDraft] = useState(() => draftOf(home));
  const [saved, setSaved] = useState(false);
  return (
    <Block>
      <Head title={t('abroad.home')} />
      <HomeFields
        draft={draft}
        onChange={(d) => {
          setDraft(d);
          setSaved(false);
        }}
      />
      <p className="mt-3 text-[13px] leading-[18px]" style={{ color: INK_FAINT }}>
        {t('abroad.homeHint')}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={actions.setHome.isPending}
          onClick={() =>
            void actions.setHome.mutateAsync(formOf(home.studentId, draft)).then(
              () => setSaved(true),
              () => undefined,
            )
          }
          className={`${pillClass('dark')} pr-4.5 disabled:opacity-35`}
        >
          {t('abroad.save')}
        </button>
        {saved && (
          <span
            className="inline-flex items-center gap-1.5 text-[14px] font-medium"
            style={{ color: GRAD.mint.ink }}
          >
            <CheckCircleIcon size={17} weight="fill" aria-hidden />
            {t('abroad.saved')}
          </span>
        )}
      </div>
    </Block>
  );
}

// ---------------------------------------------------------------------------------------------
// The family's week
// ---------------------------------------------------------------------------------------------

function WeekByFamilyClock({
  home,
  lessons,
  from,
  to,
  onMove,
}: {
  home: AbroadHome;
  lessons: LessonItem[];
  from: string;
  to: string;
  /** to the previous (-1) or the next (+1) week, when there is one */
  onMove: (delta: -1 | 1) => void;
}) {
  const { t } = useTranslation();
  const held = lessons;
  const fits = new Map(held.map((l) => [l.id, lessonFit(l, home)]));
  const live = held.filter((l) => fits.get(l.id) === 'live').length;
  // days by the family's clock: a lesson at 01:30 in Toronto is on the evening before
  const days = [...new Set(held.map((l) => clockOf(l.startsAt, home.timezone).date))].sort();
  return (
    <Block>
      <Head title={t('abroad.week')}>
        <button
          type="button"
          aria-label={t('schedule.prevWeek')}
          onClick={() => onMove(-1)}
          className={ringButton}
        >
          <CaretLeftIcon size={15} weight="bold" aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t('schedule.nextWeek')}
          onClick={() => onMove(1)}
          className={ringButton}
        >
          <CaretRightIcon size={15} weight="bold" aria-hidden />
        </button>
      </Head>
      <div className="-mt-2 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[14px] font-medium" style={{ color: INK }}>
          {`${shortDate(from)} – ${shortDate(to)}`}
        </span>
        {held.length > 0 && (
          <>
            <Label tone="lime">{t('abroad.liveCount', { count: live })}</Label>
            <Label tone="stone">{t('abroad.asyncCount', { count: held.length - live })}</Label>
          </>
        )}
      </div>
      <p className="mt-2 max-w-2xl text-[13.5px] leading-5" style={{ color: INK_SOFT }}>
        {t('abroad.weekHint')}
      </p>
      {held.length === 0 && (
        <p className="py-8 text-center text-[15px]" style={{ color: INK_SOFT }}>
          {t('abroad.noLessons')}
        </p>
      )}
      <div className="mt-2 flex flex-col">
        {days.map((day) => (
          <section key={day} className="mt-4">
            <h3
              className="mb-1 text-[15px] font-medium first-letter:uppercase"
              style={{ color: INK }}
            >
              {shortDate(day)}
            </h3>
            <ul className="flex flex-col">
              {held
                .filter((l) => clockOf(l.startsAt, home.timezone).date === day)
                .map((l) => {
                  const fit = fits.get(l.id) as LessonFit;
                  const look = subjectLook(l.subject);
                  return (
                    <li
                      key={l.id}
                      className="flex items-center gap-3 py-2.5"
                      style={{ borderTop: `1px solid ${LINE}` }}
                    >
                      <span className="w-14 shrink-0">
                        <span
                          className="block font-display text-[20px] leading-6 font-light tracking-[-0.02em]"
                          style={{ color: INK }}
                        >
                          {timeOf(l.startsAt, home.timezone)}
                        </span>
                        <span className="block text-[11px]" style={{ color: INK_FAINT }}>
                          {t('abroad.kyiv', { time: timeOf(l.startsAt, SCHOOL_TIME_ZONE) })}
                        </span>
                      </span>
                      <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={34} />
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-[15px] font-medium"
                          style={{ color: INK }}
                        >
                          {l.subject}
                        </span>
                        {/* on a phone how it fits goes under the subject, in its colour */}
                        <span
                          className="flex items-center gap-1 text-[12px] font-medium sm:hidden"
                          style={{ color: GRAD[FIT[fit].tone].ink }}
                        >
                          {(() => {
                            const Icon = FIT[fit].icon;
                            return <Icon size={12} weight="fill" aria-hidden />;
                          })()}
                          {t(`abroad.fit.${fit}`)}
                        </span>
                      </span>
                      {fit !== 'live' && (
                        <Link
                          to="/lesson-pack/$lessonId"
                          params={{ lessonId: l.id }}
                          aria-label={t('abroad.materials')}
                          title={t('abroad.materials')}
                          className={`${pillClass('light', 'sm')} max-sm:w-8 max-sm:justify-center max-sm:p-0 max-sm:ring-0`}
                        >
                          <span className="max-sm:hidden">{t('abroad.materials')}</span>
                          <PillIcon icon={ArrowUpRightIcon} size="sm" />
                        </Link>
                      )}
                      <span className="max-sm:hidden">
                        <FitBadge fit={fit} />
                      </span>
                    </li>
                  );
                })}
            </ul>
          </section>
        ))}
      </div>
    </Block>
  );
}

function FamilyView({ home }: { home: AbroadHome }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const now = useMinute();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const today = todayIso();
  const monday = isoWeekday(today) >= 6 ? addDays(weekStart(today), 7) : weekStart(today);
  const lessons =
    useLessons(
      ctx.schoolId,
      { kind: 'student', studentId: home.studentId },
      monday,
      addDays(monday, 18),
    ).data ?? [];
  // three weeks ahead; the first one with lessons opens (not an empty week of holidays)
  const { weeks, first } = weeksAhead(lessons, monday);
  const [picked, setPicked] = useState<number>();
  const shown = weeks[picked ?? first] as (typeof weeks)[number];
  const subjects = [...new Set(lessons.map((l) => l.subject))].sort((a, b) =>
    a.localeCompare(b, 'uk'),
  );
  const at = new Date(now).toISOString();
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('abroad.title')} />
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex min-w-0 flex-col gap-4">
          {/* the family's clock on sky, the globe quiet behind */}
          <section
            className="relative isolate overflow-hidden rounded-[28px] p-5 md:p-7"
            style={{ backgroundImage: grad('sky'), color: GRAD.sky.ink }}
          >
            <SubjectArt
              icon="Earth"
              tone="sky"
              size={200}
              ghost
              className="pointer-events-none absolute -top-6 -right-6 -z-10"
            />
            <p className="flex flex-wrap items-center gap-2 text-[14px] font-medium">
              {cityOf(home)}
              <Label tone="sky" white>
                {t(`abroad.track.${home.track}`)}
              </Label>
            </p>
            <p
              className="mt-2 font-display text-[40px] leading-[46px] font-light tracking-[-0.04em] md:text-[52px] md:leading-[58px]"
              style={{ color: INK }}
            >
              {t(ctx.kind === 'student' ? 'abroad.nowYou' : 'abroad.nowFamily', {
                time: timeOf(at, home.timezone),
              })}
            </p>
            <p className="mt-1 text-[15px]">
              {t('abroad.nowSchool', { time: timeOf(at, SCHOOL_TIME_ZONE) })} ·{' '}
              {zoneShift(t, home.timezone, new Date(now))}
            </p>
            <p className="mt-4 max-w-xl text-[14px] leading-[21px]" style={{ color: INK }}>
              {t(`abroad.trackHint.${home.track}`)}
            </p>
          </section>
          <WeekByFamilyClock
            home={home}
            lessons={shown.lessons}
            from={shown.from}
            to={shown.to}
            onMove={(delta) =>
              setPicked(Math.min(weeks.length - 1, Math.max(0, (picked ?? first) + delta)))
            }
          />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          {subjects.length > 0 && (
            <Block>
              <Head title={t('abroad.subjects')} />
              <p className="flex flex-wrap gap-1.5">
                {subjects.map((s) => {
                  const ours = isUkrainianStudies(s) || home.track === 'full';
                  const look = subjectLook(s);
                  return (
                    <span
                      key={s}
                      className="inline-flex h-10 items-center gap-2 rounded-[12px] pr-3 pl-1.5 text-[14px]"
                      style={
                        ours
                          ? { backgroundColor: PAPER, color: INK }
                          : { boxShadow: `inset 0 0 0 1px ${LINE}`, color: INK_SOFT }
                      }
                    >
                      <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={28} />
                      {s}
                      {!ours && <span style={{ color: INK_FAINT }}>· {t('abroad.extra')}</span>}
                    </span>
                  );
                })}
              </p>
            </Block>
          )}
          <FamilyHome home={home} />
          {preparesForNmt(child?.class?.grade) && (
            <section
              className="relative isolate overflow-hidden rounded-[28px] p-5 md:p-6"
              style={{ backgroundImage: grad('cobalt'), color: GRAD.cobalt.ink }}
            >
              <TaskArt
                kind="tests"
                tone="cobalt"
                size={130}
                ghost
                className="pointer-events-none absolute -top-3 -right-4 -z-10"
              />
              <h2
                className="font-display text-[28px] leading-[34px] font-light tracking-[-0.035em]"
                style={{ color: INK }}
              >
                {t('abroad.nmt.title')}
              </h2>
              <p className="mt-2 text-[14px] leading-[21px]" style={{ color: INK }}>
                {t('abroad.nmt.text')}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/nmt" className={pillClass('dark')}>
                  {t('abroad.nmt.practice')}
                  <PillIcon icon={ArrowUpRightIcon} />
                </Link>
                <a
                  href={ABROAD_LINKS.nmt}
                  target="_blank"
                  rel="noreferrer"
                  className={pillClass('light')}
                >
                  {t('abroad.links.nmt')}
                  <PillIcon icon={ArrowUpRightIcon} />
                </a>
              </div>
            </section>
          )}
          <Block>
            <Head title={t('abroad.links.title')} />
            <ul className="flex flex-col">
              {(['mon', 'studies', 'nmt'] as const).map((k) => (
                <li key={k} style={{ borderTop: `1px solid ${LINE}` }}>
                  <a
                    href={ABROAD_LINKS[k]}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center gap-3 py-3"
                  >
                    <span className="min-w-0 flex-1 text-[15px]" style={{ color: INK }}>
                      {t(`abroad.links.${k}`)}
                    </span>
                    <span
                      className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                    >
                      <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </Block>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The school's view: who learns from where
// ---------------------------------------------------------------------------------------------

/** «там 14:05», or «там ніч» */
export function thereNow(t: TFunction, timeZone: string, now: number) {
  const { minutes } = clockOf(new Date(now), timeZone);
  return minutes < 7 * 60 || minutes >= 22 * 60
    ? `🌙 ${t('abroad.nightNow')}`
    : t('abroad.there', { time: timeOf(new Date(now).toISOString(), timeZone) });
}

/** The time there as words, the night as a lavender label with the moon (no emoji). */
function There({ timeZone, now }: { timeZone: string; now: number }) {
  const { t } = useTranslation();
  const { minutes } = clockOf(new Date(now), timeZone);
  if (minutes < 7 * 60 || minutes >= 22 * 60)
    return (
      <Label tone="lavender" icon={MoonIcon}>
        {t('abroad.nightNow')}
      </Label>
    );
  return (
    <span className="text-[14px]" style={{ color: INK }}>
      {t('abroad.there', { time: timeOf(new Date(now).toISOString(), timeZone) })}
    </span>
  );
}

function StudentEditor({ initial, onDone }: { initial?: AbroadStudent; onDone: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const actions = useAbroadActions(ctx.schoolId);
  const people = usePeople(ctx.schoolId).data ?? [];
  const [draft, setDraft] = useState(() => draftOf(initial));
  const [studentId, setStudentId] = useState(initial?.studentId ?? '');
  const [query, setQuery] = useState('');
  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return people
      .filter((p) => p.kind === 'student')
      .filter((p) => `${p.lastName} ${p.firstName}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [people, query]);
  const picked = people.find((p) => p.id === studentId);
  return (
    <div
      className="motion-rise my-2 flex flex-col gap-4 rounded-[22px] p-4 md:p-5"
      style={{ backgroundColor: PAPER }}
    >
      {!initial && (
        <div>
          <span className={fieldLabel}>{t('abroad.student')}</span>
          {picked ? (
            <p className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-medium" style={{ color: INK }}>
                {picked.lastName} {picked.firstName}
              </span>
              <Label>{picked.classLabel}</Label>
              <button
                type="button"
                onClick={() => setStudentId('')}
                className={`${pillClass('light', 'sm')} pr-3.5`}
              >
                {t('abroad.edit')}
              </button>
            </p>
          ) : (
            <>
              <input
                className={field}
                placeholder={t('abroad.search')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {found.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {found.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setStudentId(p.id)}
                      className="inline-flex h-9 items-center rounded-full bg-white px-3.5 text-[14px] transition-colors hover:bg-[#EFECE5]"
                      style={{ color: INK }}
                    >
                      {p.lastName} {p.firstName}
                      <span className="ml-1.5" style={{ color: INK_FAINT }}>
                        {p.classLabel}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      <HomeFields draft={draft} onChange={setDraft} withTrack />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!studentId || actions.save.isPending}
          onClick={() =>
            void actions.save.mutateAsync(formOf(studentId, draft)).then(onDone, () => undefined)
          }
          className={`${pillClass('dark')} pr-4.5 disabled:opacity-35`}
        >
          {t('abroad.save')}
        </button>
        <button type="button" onClick={onDone} className={`${pillClass('light')} pr-4.5`}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

function SchoolView() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const now = useMinute();
  const list = useAbroadStudents(ctx.schoolId);
  const actions = useAbroadActions(ctx.schoolId);
  const [editing, setEditing] = useState<string>();
  const [removing, setRemoving] = useState<string>();
  const admin = ctx.kind === 'admin';
  const students = list.data ?? [];
  const byCountry = [...new Set(students.map((s) => s.country))]
    .map((code) => ({ code, count: students.filter((s) => s.country === code).length }))
    .sort((a, b) => b.count - a.count);
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('abroad.titleSchool')}
        sub={<span className="block max-w-2xl">{t('abroad.schoolIntro')}</span>}
      >
        {admin && editing !== 'new' && (
          <button type="button" onClick={() => setEditing('new')} className={pillClass('dark')}>
            {t('abroad.add')}
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* how many and from where: the count big on sky, the countries by name */}
        <section
          className="relative isolate overflow-hidden rounded-[28px] p-5 md:p-6 lg:sticky lg:top-20"
          style={{ backgroundImage: grad('sky'), color: GRAD.sky.ink }}
        >
          <SubjectArt
            icon="Earth"
            tone="sky"
            size={150}
            ghost
            className="pointer-events-none absolute -top-5 -right-5 -z-10"
          />
          <p
            className="font-display text-[44px] leading-[50px] font-light tracking-[-0.04em]"
            style={{ color: INK }}
          >
            {t('abroad.students', { count: students.length })}
          </p>
          <p className="text-[16px]">{t('abroad.countries', { count: byCountry.length })}</p>
          {byCountry.length > 0 && (
            <ul className="mt-4 flex flex-col rounded-[18px] bg-white/80 px-4 py-1">
              {byCountry.map((c, i) => (
                <li
                  key={c.code}
                  className="flex items-center justify-between gap-2 py-2 text-[14px]"
                  style={{ borderTop: i ? `1px solid ${LINE}` : undefined, color: INK }}
                >
                  {countryOf(c.code)?.name ?? c.code}
                  <span style={{ color: INK_SOFT }}>{c.count}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <Block>
          {editing === 'new' && <StudentEditor onDone={() => setEditing(undefined)} />}
          {list.isPending && (
            <div
              className="h-[300px] animate-pulse rounded-[22px]"
              style={{ backgroundColor: PAPER }}
              aria-busy
            />
          )}
          {!list.isPending && students.length === 0 && (
            <div className="flex flex-col items-center py-10 text-center">
              <SubjectArt icon="Earth" tone="sky" size={88} />
              <p className="mt-3 text-[16px]" style={{ color: INK_SOFT }}>
                {t('abroad.empty')}
              </p>
            </div>
          )}
          <ul className="flex flex-col">
            {students.map((s, i) => (
              <li
                key={s.studentId}
                className="py-3.5"
                style={{ borderTop: i ? `1px solid ${LINE}` : undefined }}
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="min-w-[220px] flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-[15.5px] font-medium" style={{ color: INK }}>
                        {s.lastName} {s.firstName}
                      </span>
                      <Label>{s.classLabel}</Label>
                    </span>
                    <span className="mt-0.5 block text-[12.5px]" style={{ color: INK_SOFT }}>
                      {t(`abroad.track.${s.track}`)} ·{' '}
                      {t('abroad.since', { date: longDate(s.since) })}
                    </span>
                  </span>
                  <span className="w-[180px] min-w-0">
                    <span className="block truncate text-[14px]" style={{ color: INK }}>
                      {cityOf(s)}
                      {s.city && (
                        <span style={{ color: INK_SOFT }}> · {countryOf(s.country)?.name}</span>
                      )}
                    </span>
                    <span className="block text-[12px]" style={{ color: INK_FAINT }}>
                      {zoneShift(t, s.timezone, new Date(now))}
                    </span>
                  </span>
                  <span className="w-[96px]">
                    <There timeZone={s.timezone} now={now} />
                  </span>
                  {admin && (
                    <span className="flex gap-1.5">
                      <button
                        type="button"
                        aria-expanded={editing === s.studentId}
                        onClick={() =>
                          setEditing(editing === s.studentId ? undefined : s.studentId)
                        }
                        className={`${pillClass('light', 'sm')} pr-3.5`}
                      >
                        {t('abroad.edit')}
                      </button>
                      <button
                        type="button"
                        aria-expanded={removing === s.studentId}
                        onClick={() =>
                          setRemoving(removing === s.studentId ? undefined : s.studentId)
                        }
                        className={`${pillClass('light', 'sm')} pr-3.5`}
                      >
                        {t('abroad.remove')}
                      </button>
                    </span>
                  )}
                </div>
                {removing === s.studentId && (
                  <div
                    className="motion-rise mt-3 flex flex-wrap items-center gap-2 rounded-[18px] p-3 pl-4"
                    style={{ backgroundColor: PAPER }}
                  >
                    <span className="mr-auto text-[14px]" style={{ color: INK }}>
                      {t('abroad.removeAsk')}
                    </span>
                    <button
                      type="button"
                      onClick={() => actions.remove.mutate(s.studentId)}
                      className={`${pillClass('dark', 'sm')} pr-3.5`}
                    >
                      {t('abroad.remove')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoving(undefined)}
                      className={`${pillClass('light', 'sm')} pr-3.5`}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                )}
                {editing === s.studentId && (
                  <StudentEditor initial={s} onDone={() => setEditing(undefined)} />
                )}
              </li>
            ))}
          </ul>
        </Block>
      </div>
    </div>
  );
}

/** «Навчання з-за кордону»: the family's week by its clock; for the school — who learns from where. */
export function AbroadPage() {
  const ctx = useCurrentContext();
  const child = ctx.kind === 'student' || ctx.kind === 'guardian' ? ctx.child : undefined;
  const home = useAbroadHome(child?.memberId ?? '');
  if (!child) return <SchoolView />;
  if (home.isPending)
    return <div className="h-[320px] animate-pulse rounded-[28px] bg-white" aria-busy />;
  return home.data ? <FamilyView home={home.data} /> : null;
}

function AbroadCardBody({
  home,
  self,
  className,
}: {
  home: AbroadHome;
  self: boolean;
  className: string;
}) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const now = useMinute();
  const today = todayIso();
  const lessons =
    useLessons(ctx.schoolId, { kind: 'student', studentId: home.studentId }, today, today).data ??
    [];
  const live = lessons.filter(
    (l) => l.status !== 'cancelled' && lessonFit(l, home) === 'live',
  ).length;
  const at = new Date(now).toISOString();
  return (
    <Link
      to="/abroad"
      className={`group flex items-center gap-4 rounded-[28px] bg-white p-4 pr-5 md:pl-5 ${className}`}
    >
      <SubjectArt icon="Earth" tone="sky" size={44} />
      <span className="min-w-0 flex-1">
        <span
          className="block truncate text-[16px] leading-[22px] font-medium"
          style={{ color: INK }}
        >
          {t(self ? 'abroad.card' : 'abroad.cardFamily')} · {cityOf(home)}
        </span>
        <span className="block text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
          {t(self ? 'abroad.nowYou' : 'abroad.nowFamily', { time: timeOf(at, home.timezone) })},{' '}
          {t('abroad.nowSchool', { time: timeOf(at, SCHOOL_TIME_ZONE) })}
          {lessons.length > 0 &&
            ` · ${live > 0 ? t('abroad.cardToday', { count: live }) : t('abroad.cardNoLive')}`}
        </span>
      </span>
      <span className={`${ringButton} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}>
        <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
      </span>
    </Link>
  );
}

/** On the home page of a family abroad: their clock and how today goes. */
export function AbroadCard({
  studentId,
  self,
  className = '',
}: {
  studentId: string;
  self: boolean;
  className?: string;
}) {
  const home = useAbroadHome(studentId).data;
  return home ? <AbroadCardBody home={home} self={self} className={className} /> : null;
}
